import { Component, createApp } from 'vue';
import { LocaleMessageDictionary } from '@intlify/core-base';
import { ELanguage } from '@ringana/configs';
import { createI18n, VueMessageType } from 'vue-i18n';
import locizer, { ILocizerGetLanguages, ILocizerLoadNamespaceMessages } from 'locizer';
import App from './App.vue';
import CookieHelper from './helpers/cookie-helper';
import { ITranslationMessages } from './common/interfaces/translations.interface';
import { TLocizeNamespace, TLocizeVersion } from './common/types/index.types';
import config from '../configs/config';

const app = createApp(App as unknown as Component);

// eslint-disable-next-line quotes
declare module '@vue/runtime-core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface ComponentCustomProperties {
    $locizeListNamespace: string;
    $locizeUfeNamespace: string;
  }
}

const i18n = createI18n({
  fallbackLocale: ELanguage.DE,
  locale: CookieHelper.getLanguageCookie() || locizer.lng,
});
const isLanguageNamespaceTranslated: Record<ELanguage, Record<TLocizeNamespace, boolean>> = {
  [ELanguage.CS]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.DE]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.EN]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.ES]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.FR]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.HU]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.IT]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.NL]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.PL]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.PT]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
  [ELanguage.SK]: { 'cookie-notice-list': false, 'cookie-notice-ufe': false },
};
const messages: Record<ELanguage, Record<TLocizeNamespace, ITranslationMessages>> = {
  [ELanguage.CS]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.DE]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.EN]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.ES]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.FR]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.HU]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.IT]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.NL]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.PL]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.PT]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
  [ELanguage.SK]: { 'cookie-notice-list': {}, 'cookie-notice-ufe': {} },
};
const namespacesWithDot = {
  list: `${config.locize.namespace.list}.`,
  ufe: `${config.locize.namespace.ufe}.`,
};

const namespaces: TLocizeNamespace[] = [
  config.locize.namespace.list as TLocizeNamespace,
  config.locize.namespace.ufe as TLocizeNamespace,
];

const fixVarFormatInMessages = (object: ITranslationMessages): ITranslationMessages => {
  const output: ITranslationMessages = {};
  for(const key of Object.keys(object)) {
    if(typeof object[key] === `string`) {
      output[key] = (object[key] as string)
        .replace(/{{/g, `{`)
        .replace(/}}/g, `}`)
        // eslint-disable-next-line no-useless-escape
        .replace(/@/g, `{'@'}`);
    } else {
      output[key] = fixVarFormatInMessages(object[key] as ITranslationMessages);
    }
  }
  return output;
};
(async (): Promise<void> => {
  locizer.init({
    loadIfTranslatedOver: config.locize.translationThreshold,
    projectId: config.locize.projectId,
    referenceLng: ELanguage.DE,
    version: config.locize.version,
  });
  const promises = [];
  const locizeLanguages: ELanguage[] = [];
  await new Promise((resolve, reject) => {
    locizer.getLanguages((err: Error, languages: Record<string, ILocizerGetLanguages & { translated: Record<TLocizeVersion, number> }>) => {
      if(err) {
        reject(err);
        throw new Error(err.toString());
      }
      for(const language of Object.keys(languages)) {
        if(languages[language].translated[config.locize.version as TLocizeVersion] > config.locize.translationThreshold) {
          locizeLanguages.push(ELanguage[language.toUpperCase() as keyof typeof ELanguage]);
        }
      }
      resolve(true);
    });
  });
  for(const namespace of namespaces) {
    for(const locizeLanguage of locizeLanguages) {
      promises.push(new Promise((resolve, reject) => {
        locizer.load(namespace, locizeLanguage, (err: Error, loadedMessages: Record<ELanguage, ILocizerLoadNamespaceMessages>) => {
          if(err) {
            reject(err);
          }
          messages[locizeLanguage][namespace] = fixVarFormatInMessages(loadedMessages) as ITranslationMessages;
          isLanguageNamespaceTranslated[locizeLanguage][namespace] = true;
          resolve(true);
        });
      }));
    }
  }
  await Promise.all(promises);

  for(const language of Object.keys(messages)) {
    const typedLanguage = language as ELanguage;
    if(Object.keys(messages[typedLanguage][config.locize.namespace.ufe as TLocizeNamespace]).length === 0) {
      messages[typedLanguage][config.locize.namespace.ufe as TLocizeNamespace] = messages[ELanguage.DE][config.locize.namespace.ufe as TLocizeNamespace];
    }
    i18n.global.setLocaleMessage(language, messages[typedLanguage] as LocaleMessageDictionary<VueMessageType>);
  }

  // Set i18n instance on app
  app.use(i18n);

  app.provide(`locizeListNamespace`, namespacesWithDot.list);
  app.config.globalProperties.$locizeListNamespace = namespacesWithDot.list;
  app.provide(`locizeUfeNamespace`, namespacesWithDot.ufe);
  app.config.globalProperties.$locizeUfeNamespace = namespacesWithDot.ufe;

  app.mount(`#ccn`);
})();

export {
  i18n,
  isLanguageNamespaceTranslated,
  namespacesWithDot,
  messages as translationValues,
};
